// ─── Forms module (Task 1 — Forms Foundation) ─────────────────────────────────
//
// Plain TypeScript types only — no Zod, no framework imports. The same shapes
// are consumed by the api, the web app, and the host/embed bundles (Task 4),
// so this file stays portable. Runtime validation lives in
// apps/api/src/modules/forms/forms.schemas.ts via Zod.

// ─── Enums ────────────────────────────────────────────────────────────────────

export type FormPathType = 'CANDIDATE' | 'CONTACT';

export type FormStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type FormSource = 'CAMPAIGN' | 'DIRECT' | 'EMBED' | 'GATED_CONTENT';

export type FormDeliveryMode = 'AUTO_DOWNLOAD' | 'EMAIL';

export type FormDocumentType = 'GATED_CONTENT' | 'RESUME' | 'ID' | 'OTHER';

export type FormBounceState = 'NONE' | 'SOFT' | 'HARD';

export type FormConsentMode = 'DISABLED' | 'NOTICE' | 'CHECKBOX';

// ─── Field schema ─────────────────────────────────────────────────────────────

export type FormFieldType =
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'EMAIL'
  | 'PHONE'
  | 'NUMBER'
  | 'SINGLE_SELECT'
  | 'MULTI_SELECT'
  | 'DATE'
  | 'FILE_UPLOAD'
  | 'ADDRESS'
  | 'CONSENT_CHECKBOX';

export interface FormFieldValidationRules {
  /// Minimum value (numbers) or length (strings); inclusive.
  min?: number;
  /// Maximum value (numbers) or length (strings); inclusive.
  max?: number;
  /// Regex pattern (string source). Compiled at validation time.
  regex?: string;
  /// Candidate-path only. Reject submission if the new value differs from
  /// the existing value by more than this percentage (0–100). Used by
  /// Task 2's integrity layer. Scope §6.
  percentage_delta?: number;
  /// Candidate-path only. Reject submission if the absolute delta exceeds
  /// this. Alternative to percentage_delta for fields where ratios don't
  /// make sense (e.g. notice period weeks).
  max_absolute_delta?: number;
  /// Candidate-path only. Defaults to true for scalar fields. When true,
  /// a blank submitted value preserves the existing value rather than
  /// wiping it. Scope §5.
  blank_out_protected?: boolean;
  /// Candidate-path only. When the change exceeds this threshold (same
  /// units as percentage_delta — percentage 0–100), flag is_significant
  /// on the resulting FormSubmissionFieldChange row. Drives recruiter
  /// notifications (Task 6). Independent of percentage_delta — flagging
  /// is soft, percentage_delta is a hard reject. Scope §6.
  significant_change_threshold?: number;
}

export interface FormFieldConditionalLogic {
  /// The key of another field in the same form. Forward-only:
  /// must reference a field that appears *before* this one in the schema.
  field_key: string;
  operator: 'equals' | 'not_equals' | 'contains';
  /// The value to compare against. Type depends on the referenced field.
  value: string | number | boolean;
}

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormFieldDefinitionCommon {
  /// Stable identifier for the field on the wire. Slug-formatted
  /// ([a-z0-9_]+). For candidate-path updateable fields, the key matches
  /// a known JT candidate column or "custom_attribute:<name>".
  key: string;
  label: string;
  help_text?: string;
  required: boolean;
  conditional_logic?: FormFieldConditionalLogic;
  /// Candidate-path only. When true (default), the value writes back to
  /// the candidate record. When false, the field renders read-only.
  updateable_on_candidate_path?: boolean;
  validation_rules?: FormFieldValidationRules;
}

export interface FormFieldShortText extends FormFieldDefinitionCommon {
  type: 'SHORT_TEXT';
}

export interface FormFieldLongText extends FormFieldDefinitionCommon {
  type: 'LONG_TEXT';
}

export interface FormFieldEmail extends FormFieldDefinitionCommon {
  type: 'EMAIL';
}

export interface FormFieldPhone extends FormFieldDefinitionCommon {
  type: 'PHONE';
}

export interface FormFieldNumber extends FormFieldDefinitionCommon {
  type: 'NUMBER';
  step?: number;
}

export interface FormFieldSingleSelect extends FormFieldDefinitionCommon {
  type: 'SINGLE_SELECT';
  options: FormFieldOption[];
}

export interface FormFieldMultiSelect extends FormFieldDefinitionCommon {
  type: 'MULTI_SELECT';
  options: FormFieldOption[];
}

export interface FormFieldDate extends FormFieldDefinitionCommon {
  type: 'DATE';
}

export interface FormFieldFileUpload extends FormFieldDefinitionCommon {
  type: 'FILE_UPLOAD';
  /// Curated preset that maps to a server-enforced MIME subset (Task 2).
  /// The server is always the source of truth; this just steers the user.
  accept_preset?: 'RESUME' | 'ID' | 'IMAGE' | 'ANY_DOC';
  /// The JobTetris document type the uploaded file is saved as (JT1-1923). Default 'resume'.
  jt_document_type?: FormJtDocumentType;
}

/// JobTetris DocumentType values a form upload can be saved as (JT1-1923). JobTetris refuses
/// anything outside its own enum, so these are its exact lowercase values.
export type FormJtDocumentType =
  | 'resume'
  | 'cover_letter'
  | 'certificate'
  | 'visa'
  | 'passport'
  | 'driving_license'
  | 'other_license'
  | 'other';

export const FORM_JT_DOCUMENT_TYPES: { value: FormJtDocumentType; label: string }[] = [
  { value: 'resume', label: 'Resume' },
  { value: 'cover_letter', label: 'Cover letter' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'visa', label: 'Visa' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: 'Driving licence' },
  { value: 'other_license', label: 'Other licence' },
  { value: 'other', label: 'Other' },
];

export const DEFAULT_FORM_JT_DOCUMENT_TYPE: FormJtDocumentType = 'resume';

export interface FormFieldAddress extends FormFieldDefinitionCommon {
  type: 'ADDRESS';
}

export interface FormFieldConsentCheckbox extends FormFieldDefinitionCommon {
  type: 'CONSENT_CHECKBOX';
  /// Optional override for the consent text on this specific checkbox.
  /// When absent, the form-level consent text from §12 is used.
  text_override?: string;
}

export type FormFieldDefinition =
  | FormFieldShortText
  | FormFieldLongText
  | FormFieldEmail
  | FormFieldPhone
  | FormFieldNumber
  | FormFieldSingleSelect
  | FormFieldMultiSelect
  | FormFieldDate
  | FormFieldFileUpload
  | FormFieldAddress
  | FormFieldConsentCheckbox;

// ─── Brand override ───────────────────────────────────────────────────────────

/// Sparse overlay on the org's BrandKit. Only keys present override the
/// org default; everything else inherits.
export interface FormBrandOverride {
  logo_url?: string | null;
  primary_color?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  heading_font?: string;
  body_font?: string;
  button_radius_px?: number;
  content_width_px?: number;
}

/// The resolved brand after merging FormBrandOverride onto the org BrandKit.
/// Snapshotted onto FormVersion.brand_snapshot at publish time.
export interface ResolvedFormBrand {
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  heading_font: string;
  body_font: string;
  button_radius_px: number;
  content_width_px: number;
  /// Carried through so the email and footer templates can use it.
  company_name: string | null;
}

// ─── Consent ──────────────────────────────────────────────────────────────────

/// Snapshot stored on FormVersion.consent_snapshot when consent is on at
/// publish time.
export interface FormConsentSnapshot {
  mode: Exclude<FormConsentMode, 'DISABLED'>;
  text: string;
}

/// Stored on FormSubmission.consent_state at submit time.
export interface FormSubmissionConsentState {
  mode: FormConsentMode;
  text: string | null;
  granted: boolean;
  granted_at: string | null;
}

// ─── Submission payload (the wire shape from the public endpoint) ────────────

export interface FormSubmissionPayload {
  field_values: Record<string, unknown>;
  /// Present when the published form has consent_snapshot non-null.
  consent?: {
    granted: boolean;
    text: string;
  };
  /// Cloudflare Turnstile token. Required on contact-path submits.
  captcha_token?: string;
}

// ─── Submission response (the iframe contract — Task 2) ──────────────────────

export interface FormSubmissionGatedDelivery {
  delivery_mode: FormDeliveryMode;
  /// Only present for AUTO_DOWNLOAD mode. Signed URL on the app host
  /// (`/f/api/download/...`) that 302s to the files host. Valid for the
  /// delivery's TTL.
  download_url?: string;
}

export interface FormSubmissionSuccess {
  status: 'ok';
  submission_id: string;
  /// Present when the form has a gated document configured.
  gated?: FormSubmissionGatedDelivery;
}

export interface FormSubmissionRejected {
  status: 'rejected';
  reason: 'validation' | 'captcha' | 'token_expired' | 'already_submitted' | 'bounds';
  /// Per-field error map when reason = 'validation' or 'bounds'.
  field_errors?: Record<string, string>;
}

export type FormSubmissionResponse = FormSubmissionSuccess | FormSubmissionRejected;
